










































































































import Vue from 'vue';
import { mapActions } from 'vuex';
import MStoreFormSelect from '@/components/shared/MStoreFormSelect.vue';
import {
  DocumentTypeModel,
  CabinetReferenceModel,
  BaseResultModel,
  ArenaSignDocumentTypeUpdateModel,
  Configuration,
  Dictionary,
  ArenaSignApi,
  ArenaSignDocumentTypeConfigurationModel,
  StickySettingsActions,
  StickyStoreNames,
  StickySettingsFields,
  MStoreConstants
} from '@/types';
import { DocuSignConfigurationService } from '@/services/docuSign/DocuSignConfigurationService';
import { ScriveConfigurationService } from '@/services/scrive/ScriveConfigurationService';
import SnotifyConfiguration from '@/classes/SnotifyConfiguration';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import { ComponentHelper, MTablehelper, RouteHelpers, confirmationHelper, FormFieldValidation, ObjectHelper } from '@/mixins';

export default Vue.extend({
  name: 'ArenaSignEditDocumentTypes',
  mixins: [ComponentHelper, MTablehelper, RouteHelpers, confirmationHelper, FormFieldValidation, ObjectHelper],
  data() {
    return {
      pageIsLoading: true,
      submitIsLoading: false,
      isLoading: false,
      hasError: false,
      errorMessage: '',
      documentTypes: [] as DocumentTypeModel[],
      arenaSignDocumentTypeConfigurationModel: {} as ArenaSignDocumentTypeConfigurationModel,
      documentTypeId: null,
      keyBusinessReferenceField: ''
    };
  },
  methods: {
    ...mapActions('applicationDirtyStore', ['setApplicationDirty']),
    ...mapActions(StickyStoreNames.ScriveTemplateManagement, {
      setScriveStickySetField: StickySettingsActions.SetStickyStateField
    }),
    ...mapActions(StickyStoreNames.DocuSignTemplateManagement, {
      setDocuSignStickySetField: StickySettingsActions.SetStickyStateField
    }),
    async getData(this: any) {
      this.isLoading = true;

      // tslint:disable-next-line:max-line-length
      this.arenaSignDocumentTypeConfigurationModel = await this.getDocumentTypeConfigurationAsync({
        documentTypeId: this.documentTypeId
      } as ArenaSignDocumentTypeConfigurationModel);
      this.documentTypes = this.arenaSignDocumentTypeConfigurationModel.documentTypes;
      this.documentTypeId = this.arenaSignDocumentTypeConfigurationModel.documentTypeId;
      this.keyBusinessReferenceField = this.arenaSignDocumentTypeConfigurationModel.keyBusinessReferenceField;

      this.isLoading = false;
    },
    async onSubmit(this: any) {
      this.resetFormValidation();
      try {
        const isValid = await this.$validator.validate();
        if (isValid) {
          this.submitIsLoading = true;

          const result: BaseResultModel = await this.updateDocumentTypeAsync({
            documentTypeId: this.documentTypeId,
            keyBusinessReferenceField: this.keyBusinessReferenceField
          } as ArenaSignDocumentTypeUpdateModel);

          if (result.hasNotification && !result.showToast) {
            this.addFormValidationError(result.notification);
            this.submitIsLoading = false;
            return;
          }

          SnotifyConfiguration.showSuccess('Document type updated successfully.');

          this.setApplicationDirty(false);
          await this.getData();
        }
      } catch (error) {
        this.$log('Error updating document type', error);
      } finally {
        this.submitIsLoading = false;
      }
    },
    async initialise(this: any) {
      try {
        this.pageIsLoading = true;
        await this.getData();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.pageIsLoading = false;
      }
    },
    navigateToTemplateManagement(this: any) {
      const selectedDocumentTypeId = this.documentTypeId;
      switch (this.arenaSignApi) {
        case ArenaSignApi.DocuSign: {
          this.setDocuSignStickySetField({
            field: StickySettingsFields.DocumentTypeId,
            value: selectedDocumentTypeId
          });
          break;
        }
        case ArenaSignApi.Scrive: {
          this.setScriveStickySetField({ field: StickySettingsFields.DocumentTypeId, value: selectedDocumentTypeId });
          break;
        }
      }
      const param = {} as Dictionary<any>;
      param.documentTypeId = this.documentTypeId;
      this.$router.push({
        name: this.arenaSignApi === ArenaSignApi.DocuSign ? Configuration.DocuSign.Template.Management.name : Configuration.Scrive.Template.Management.name,
        params: param
      });
    },
    async onCancel(this: any, reset: () => void) {
      try {
        const confirmed = !(await this.hasUnsavedChangesToKeepAsync(this.$bvModal, true));
        if (!confirmed) {
          return;
        }
        this.setApplicationDirty(false);
        reset();
        await this.getData();
      } catch (error) {
        this.$log('failed to confirm unsaved changes');
        return false;
      }
    },
    async getDocumentTypeConfigurationAsync(this: any): Promise<ArenaSignDocumentTypeConfigurationModel> {
      if (this.arenaSignApi === ArenaSignApi.DocuSign) {
        return await DocuSignConfigurationService.getDocumentTypeConfigurationAsync({
          documentTypeId: this.documentTypeId
        } as ArenaSignDocumentTypeConfigurationModel);
      }

      return await ScriveConfigurationService.getDocumentTypeConfigurationAsync({
        documentTypeId: this.documentTypeId
      } as ArenaSignDocumentTypeConfigurationModel);
    },
    async updateDocumentTypeAsync(this: any): Promise<BaseResultModel> {
      if (this.arenaSignApi === ArenaSignApi.DocuSign) {
        return await DocuSignConfigurationService.updateDocumentTypeAsync({
          documentTypeId: this.documentTypeId,
          keyBusinessReferenceField: this.keyBusinessReferenceField
        } as ArenaSignDocumentTypeUpdateModel);
      }

      return await ScriveConfigurationService.updateDocumentTypeAsync({
        documentTypeId: this.documentTypeId,
        keyBusinessReferenceField: this.keyBusinessReferenceField
      } as ArenaSignDocumentTypeUpdateModel);
    }
  },
  computed: {
    documentTypeReferences(this: any): CabinetReferenceModel[] {
      if (this.documentTypeId !== null && !this.isNullOrEmpty(this.documentTypes)) {
        const documentType: DocumentTypeModel = this.documentTypes.find((x: DocumentTypeModel) => x.id === this.documentTypeId);
        if (documentType !== null) {
          return documentType.references;
        }
      }
      return [] as CabinetReferenceModel[];
    },
    templateCount(this: any) {
      return this.arenaSignDocumentTypeConfigurationModel ? this.arenaSignDocumentTypeConfigurationModel.templateCount : '';
    },
    arenaSignApi(this: any): ArenaSignApi {
      if (this.$route.name.startsWith('scrive')) {
        return ArenaSignApi.Scrive;
      }
      return ArenaSignApi.DocuSign;
    },
    arenaSignApiName(this: any): string {
      return ArenaSignApi[this.arenaSignApi];
    }
  },
  watch: {
    async documentTypeId(this: any, documentTypeId: number | undefined, previousDocumentTypeId) {
      if (previousDocumentTypeId == null) {
        return;
      }
      this.getData();
    }
  },
  created(this: any) {
    this.APP_NAME = MStoreConstants.AppName;
  },
  components: {
    LoadingSpinner,
    MStoreFormSelect
  },
  mounted() {
    this.initialise();
  }
});
